import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import * as R from 'ramda'
import SignaturePad from 'signature_pad'

import { Nil } from '../../types'
import {
  SIGNATURE_DRAWER_HEIGHT,
  SIGNATURE_DRAWER_WIDTH,
} from './signatureUtils'

const useStyles = makeStyles(
  theme => ({
    canvas: {
      width: '100%',
      height: '100%',
    },
    wrapper: {
      border: theme.constants.handwritingBorder,
      width: (props: SignatureDrawerProps) =>
        props.width ?? SIGNATURE_DRAWER_WIDTH,
      height: (props: SignatureDrawerProps) =>
        props.height ?? SIGNATURE_DRAWER_HEIGHT,
      maxWidth: '100%',
    },
    wrapperError: {
      border: theme.constants.handwritingBorderError,
    },
  }),
  { name: 'SignatureDrawer' },
)

const aspectRatio = Math.max(window.devicePixelRatio || 1, 1)

const exportSignatureToSvg = (padInstance?: SignaturePad) =>
  padInstance?.toDataURL('image/svg+xml') || ''
const exportSignatureToPng = (padInstance?: SignaturePad) =>
  padInstance?.toDataURL('image/png') || ''
const clearSignature = (padInstance?: SignaturePad) => padInstance?.clear()

const updateCanvasAspectRatio = (canvas: HTMLCanvasElement) => {
  canvas.width = canvas.offsetWidth * aspectRatio
  canvas.height = canvas.offsetHeight * aspectRatio
  const context = canvas.getContext('2d')
  if (context) {
    context.scale(aspectRatio, aspectRatio)
  }
}

export interface SignatureDrawerHandle {
  clear: () => void
  getIsValid: () => boolean
  getPng: () => string
  getSvg: () => string
  validate: () => boolean
}

export interface SignatureDrawerProps {
  readonly className?: string
  readonly disabled?: boolean
  readonly height?: number
  readonly initialDataUrl?: string | Nil
  readonly onBeginStroke?: () => void
  readonly width?: number
}

const SignatureDrawer = forwardRef<SignatureDrawerHandle, SignatureDrawerProps>(
  function SignatureDrawer(
    { className, height, width, initialDataUrl, onBeginStroke = R.F, disabled },
    rootRef,
  ) {
    const classes = useStyles({ width, height })

    const canvasRef = useRef<HTMLCanvasElement>(null)

    const [padInstance, setPadInstance] = useState<SignaturePad>()
    const [isValid, setIsValid] = useState(true)

    const setSignatureData = () => {
      if (padInstance) {
        clearSignature(padInstance)
        if (canvasRef.current && initialDataUrl) {
          padInstance.fromDataURL(initialDataUrl, {
            ratio: aspectRatio,
            width: canvasRef.current.offsetWidth,
            height: canvasRef.current.offsetHeight,
          })
        }
      }
    }

    useEffect(() => {
      if (!padInstance && canvasRef.current) {
        const initialSignaturePad = new SignaturePad(canvasRef.current)
        setPadInstance(initialSignaturePad)
      }
    }, [canvasRef.current])

    useEffect(() => {
      setSignatureData()
    }, [initialDataUrl, padInstance, canvasRef.current])

    useEffect(() => {
      if (canvasRef.current) {
        updateCanvasAspectRatio(canvasRef.current)
      }
      clearSignature(padInstance)
    }, [aspectRatio, padInstance])

    useEffect(() => {
      if (disabled) {
        padInstance?.off()
      } else {
        padInstance?.on()
      }
    }, [disabled])

    const getIsValid = () => !padInstance?.isEmpty()

    const validate = () => {
      const valid = getIsValid()
      setIsValid(valid)

      return valid
    }

    useImperativeHandle(rootRef, () => ({
      validate,
      getIsValid,
      getPng: () => exportSignatureToPng(padInstance),
      getSvg: () => exportSignatureToSvg(padInstance),
      clear: () => clearSignature(padInstance),
    }))

    return (
      <div
        className={classNames(classes.wrapper, className, {
          [classes.wrapperError]: !isValid,
        })}
      >
        <canvas
          className={classes.canvas}
          ref={canvasRef}
          onPointerUp={onBeginStroke}
        />
      </div>
    )
  },
)

export default SignatureDrawer
